/**
 * `ParticipantFieldService` — `/api/v1/events/{eventId}/participant-fields/*`.
 *
 * Конструктор формы участника: дополнительные поля, настраиваемые под
 * конкретное мероприятие. Это то, что в scan-backend было тремя жёстко зашитыми
 * колонками («прошёл взвешивание», «оплатил взнос», «предоставил справку»).
 *
 * ⚠️ ГЛАВНОЕ СЛЕДСТВИЕ ДЛЯ ФРОНТА: колонки таблицы участников, поля формы,
 * фильтры и столбцы шаблона импорта **строятся в рантайме** из ответа `list()`.
 * Ни одного захардкоженного названия графы в этом приложении нет — и появиться
 * не должно, иначе весь смысл конструктора теряется на первом же соревновании
 * без взвешивания.
 *
 * ПРАВА те же, что и на мероприятие: admin и owner настраивают, viewer читает
 * (без определений полей он не сможет даже отрисовать список), не участник
 * получает 404 `EVENT_NOT_FOUND`.
 */

import { http } from '@/lib/http'
import type {
  CreateParticipantFieldRequest,
  CreateParticipantFieldResponse,
  DeleteParticipantFieldResponse,
  ListParticipantFieldsResponse,
  ReorderParticipantFieldsRequest,
  ReorderParticipantFieldsResponse,
  UpdateParticipantFieldRequest,
  UpdateParticipantFieldResponse,
} from '@/types/api'

function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/participant-fields${suffix}`
}

export const participantFieldsApi = {
  /**
   * `GET .../participant-fields` — все поля мероприятия **в порядке формы**.
   * Читает любой участник мероприятия.
   *
   * Порядок — `(position, createdAt)`, и это весь порядок, который есть: по
   * нему строятся форма, столбцы таблицы, столбцы шаблона импорта и столбцы
   * выгрузки. Сортировать этот список на клиенте нельзя — он разъедется с
   * файлом. Менять порядок можно только через `reorder()`.
   */
  async list(eventId: string, signal?: AbortSignal): Promise<ListParticipantFieldsResponse> {
    const { data } = await http.get<ListParticipantFieldsResponse>(path(eventId), { signal })
    return data
  },

  /**
   * `POST .../participant-fields` — admin или owner.
   * `409 PARTICIPANT_FIELD_ALREADY_EXISTS` с `metadata.name` — название занято
   * (сравнение без учёта регистра). Занято **в области видимости**, а не в
   * мероприятии: чем именно, говорит `metadata.conflict_scope`
   * (`scope` / `shared` / `otherRole`, у последнего ещё `conflict_role_id`) —
   * см. `ParticipantField.name` и `participantFieldConflictText()`.
   */
  async create(
    eventId: string,
    body: CreateParticipantFieldRequest,
  ): Promise<CreateParticipantFieldResponse> {
    const { data } = await http.post<CreateParticipantFieldResponse>(path(eventId), body)
    return data
  },

  /**
   * `PUT .../participant-fields/{id}` — название, роль, подсветка,
   * обязательность, описание и — для `select` — список вариантов.
   *
   * ⚠️ **Тип поля не меняется**, и в теле его нет: у уже проставленных значений
   * нет осмысленного перевода из галочки в текст, и «поменяли тип — потеряли
   * данные» не то поведение, которое стоит делать доступным одним PUT'ом.
   * Нужен другой тип — заводится другое поле.
   *
   * ⚠️ Тело НЕ частичное целиком: пустая строка в `participantRoleId` снимает
   * привязку, пустая `description` убирает подсказку, `options` заменяет список
   * вариантов ЦЕЛИКОМ. Присылайте желаемое состояние, а не дельту.
   */
  async update(
    eventId: string,
    id: string,
    body: UpdateParticipantFieldRequest,
  ): Promise<UpdateParticipantFieldResponse> {
    const { data } = await http.put<UpdateParticipantFieldResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
      body,
    )
    return data
  },

  /**
   * `POST .../participant-fields:reorder` — новый порядок формы. admin/owner.
   *
   * ⚠️ **`fieldIds` ОБЯЗАН СОДЕРЖАТЬ ВСЕ ПОЛЯ МЕРОПРИЯТИЯ РОВНО ПО РАЗУ.**
   * Частичный список — `VALIDATION_FAILED`, а не «переставить только эти»:
   * позиция это свойство ПОРЯДКА, а не поля, и, приняв половину, сервер обязан
   * был бы додумать, куда девать остальных. Пишется одной транзакцией.
   *
   * Возвращает список целиком и в новом порядке — тот же, что вернул бы
   * `list()` сразу после. Стор кладёт в `items` ответ, а не собственную
   * догадку: иначе при отвергнутом запросе экран и сервер разошлись бы молча.
   */
  async reorder(
    eventId: string,
    fieldIds: string[],
  ): Promise<ReorderParticipantFieldsResponse> {
    const body: ReorderParticipantFieldsRequest = { fieldIds }
    const { data } = await http.post<ReorderParticipantFieldsResponse>(
      // ⚠️ `:reorder` — это ЧАСТЬ ПОСЛЕДНЕГО СЕГМЕНТА пути (custom method в
      // стиле AIP-136), а не отдельный сегмент и не query. Двоеточие в пути
      // легально и не кодируется — `encodeURIComponent` здесь применён только к
      // id мероприятия, и применять его к суффиксу нельзя: `%3Areorder` уехал
      // бы в другой маршрут.
      path(eventId, ':reorder'),
      body,
    )
    return data
  },

  /**
   * `DELETE .../participant-fields/{id}`.
   *
   * ⚠️ Вместе с полем каскадом уходят **все проставленные по нему значения** у
   * всех участников мероприятия: «удалить графу взвешивания» стирает отметки о
   * взвешивании у всех. Это осознанно — поле, которого больше нет, не может
   * иметь значений, а хранить их «на всякий случай» означало бы, что повторное
   * создание поля с тем же названием воскресит чужие галочки.
   *
   * Подтверждение обязано сказать это словами, а не спросить «вы уверены?».
   */
  async remove(eventId: string, id: string): Promise<DeleteParticipantFieldResponse> {
    const { data } = await http.delete<DeleteParticipantFieldResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
    )
    return data
  },
}
